import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminResourceHubCreateAction,
  adminResourceHubGetAction,
  adminResourceHubUpdateAction
} from "../Action/action";
import config from "../coreFIles/config";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function digitsOnly(value) {
  return String(value || "").replace(/\D+/g, "");
}

function isPdfFile(file) {
  if (!file) return false;
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
}

function isImageFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  return type.startsWith("image/") || /\.(png|jpg|jpeg|webp|gif)$/i.test(file.name || "");
}

export default function ResourceHubForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const thumbnailRef = useRef(null);
  const pdfRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [title, setTitle] = useState("");
  const [itemType, setItemType] = useState("resource_hub");
  const [sortOrder, setSortOrder] = useState("0");
  const [status, setStatus] = useState(1);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [existingThumbnail, setExistingThumbnail] = useState("");
  const [existingPdf, setExistingPdf] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminResourceHubGetAction(id);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load resource hub item.");
          return;
        }
        const d = res.data || {};
        setTitle(d.title || "");
        setItemType(String(d.type || "").toLowerCase() === "how_to" ? "how_to" : "resource_hub");
        setSortOrder(String(d.sort_order ?? 0));
        setStatus(d.status !== undefined && d.status !== null ? (Number(d.status) ? 1 : 0) : 1);
        setExistingThumbnail(d.thumbnail || "");
        setExistingPdf(d.pdf_file || "");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load resource hub item.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = itemType === "how_to" ? "Button text is required." : "Title is required.";
    if (!isEdit && itemType === "resource_hub" && !thumbnailFile) errs.thumbnail = "Thumbnail is required.";
    if (!isEdit && !pdfFile) errs.pdf = "PDF file is required.";
    if (thumbnailFile && !isImageFile(thumbnailFile)) errs.thumbnail = "Thumbnail must be an image file.";
    if (pdfFile && !isPdfFile(pdfFile)) errs.pdf = "PDF file must be a valid PDF.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = async () => {
    if (saving) return;
    setError("");
    if (!validate()) return;

    const fd = new FormData();
    fd.append("title", String(title || "").trim());
    fd.append("item_type", itemType);
    fd.append("sort_order", String(Number(sortOrder || 0)));
    fd.append("status", String(status ? 1 : 0));
    fd.append("type", "document");
    if (thumbnailFile) fd.append("thumbnail", thumbnailFile);
    if (pdfFile) fd.append("pdf_file", pdfFile);

    setSaving(true);
    try {
      const res = isEdit
        ? await adminResourceHubUpdateAction(id, fd)
        : await adminResourceHubCreateAction(fd);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Save failed");
      toast.success(isEdit ? "Resource hub item updated" : "Resource hub item created", {
        toastId: "resource-hub-save"
      });
      navigate("/resource-hub");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "resource-hub-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {(loading || saving) && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      <div className="toolbar">
        <div className="breadcrumb">
          <Link to="/resource-hub">Resource Hub</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"} Resource</span>
        </div>
        <Link to="/resource-hub" className="btn btn-primary">
          Back
        </Link>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">!</span>
          <div className="banner-text">
            <strong>Save failed.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="settings-section">
          <h3>Resource Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label>
                {itemType === "how_to" ? "Button text" : "Title"} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...(p || {}), title: "" }));
                }}
                placeholder={itemType === "how_to" ? "Button text shown on the How To screen" : "Resource title"}
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? <div className="text-danger field-error">{fieldErrors.title}</div> : null}
            </div>
            <div className="form-group">
              <label>
                Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={itemType}
                onChange={(e) => {
                  setItemType(e.target.value);
                  setFieldErrors((p) => ({ ...(p || {}), thumbnail: "", title: "" }));
                }}
              >
                <option value="resource_hub">Resource Hub</option>
                <option value="how_to">How To</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                value={sortOrder}
                onChange={(e) => setSortOrder(digitsOnly(e.target.value))}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={status} onChange={(e) => setStatus(Number(e.target.value))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Thumbnail {itemType === "resource_hub" && !isEdit ? <span className="text-danger">*</span> : null}
              </label>
              <div
                className="upload-zone"
                role="button"
                tabIndex={0}
                onClick={() => thumbnailRef.current?.click?.()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") thumbnailRef.current?.click?.();
                }}
                style={fieldErrors.thumbnail ? { borderColor: "var(--danger)" } : undefined}
              >
                <div className="upload-zone-icon"><span style={{ color: "var(--primary)", display: "inline-flex" }} aria-hidden="true">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="9" cy="9" r="2.5" />
                      <path d="M3 16l5-5 4 4 3-3 6 6" />
                    </svg>
                  </span></div>
                <p>{thumbnailFile ? thumbnailFile.name : "Click to upload thumbnail"}</p>
                <p className="upload-zone-hint">
                  {itemType === "how_to" ? "Optional for How To. PNG, JPG, WEBP, GIF" : "PNG, JPG, WEBP, GIF"}
                </p>
              </div>
              <input
                ref={thumbnailRef}
                type="file"
                accept="image/*,.png,.jpg,.jpeg,.webp,.gif"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file && !isImageFile(file)) {
                    setThumbnailFile(null);
                    setFieldErrors((p) => ({ ...(p || {}), thumbnail: "Thumbnail must be an image file." }));
                    e.target.value = "";
                    return;
                  }
                  setThumbnailFile(file);
                  setFieldErrors((p) => ({ ...(p || {}), thumbnail: "" }));
                }}
              />
              {fieldErrors.thumbnail ? <div className="text-danger field-error">{fieldErrors.thumbnail}</div> : null}
              {existingThumbnail && !thumbnailFile ? (
                <div style={{ marginTop: 10 }}>
                  <img
                    src={resolvePublicUrl(existingThumbnail)}
                    alt="Current thumbnail"
                    style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 8 }}
                  />
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                PDF File {!isEdit ? <span className="text-danger">*</span> : null}
              </label>
              <div
                className="upload-zone"
                role="button"
                tabIndex={0}
                onClick={() => pdfRef.current?.click?.()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") pdfRef.current?.click?.();
                }}
                style={fieldErrors.pdf ? { borderColor: "var(--danger)" } : undefined}
              >
                <div className="upload-zone-icon">📎</div>
                <p>{pdfFile ? pdfFile.name : "Click to upload PDF"}</p>
                <p className="upload-zone-hint">PDF only</p>
              </div>
              <input
                ref={pdfRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file && !isPdfFile(file)) {
                    setPdfFile(null);
                    setFieldErrors((p) => ({ ...(p || {}), pdf: "PDF file must be a valid PDF." }));
                    e.target.value = "";
                    return;
                  }
                  setPdfFile(file);
                  setFieldErrors((p) => ({ ...(p || {}), pdf: "" }));
                }}
              />
              {fieldErrors.pdf ? <div className="text-danger field-error">{fieldErrors.pdf}</div> : null}
              {existingPdf && !pdfFile ? (
                <p className="small-text mt-2">
                  Current file:{" "}
                  <a href={resolvePublicUrl(existingPdf)} target="_blank" rel="noreferrer" className="action-link">
                    View PDF
                  </a>
                </p>
              ) : null}
            </div>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" type="button" disabled={saving || loading} onClick={onSave}>
              {saving ? "Saving..." : "Save Resource"}
            </button>
            <Link to="/resource-hub" className="btn btn-outline">
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
